import { useRef, useState, useEffect } from "react";
import Header from "../../commonComponents/header/Header";
import reloadImg from "../../../assets/images/reload.svg";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import { useNavigate } from "react-router-dom";
import { useNetworkResilientVideo } from "../../../hooks/useNetworkResilientVideo";

const Verification = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  useNetworkResilientVideo(videoRef);
  const [isFirstWatch, setIsFirstWatch] = useState(true);
  const [isIntroStage, setIsIntroStage] = useState(true);
  const [isReplayFullscreen, setIsReplayFullscreen] = useState(false);
  const [showTopMode, setShowTopMode] = useState(false);

  useEffect(() => {
    if (isIntroStage && !isReplayFullscreen) {
      const t = setTimeout(() => setShowTopMode(true), 2000);
      return () => clearTimeout(t);
    }
  }, [isIntroStage, isReplayFullscreen]);

  const screenshotFlag =
    sessionStorage.getItem("mobileProctoringScreenshot") === "true";
  const videoFlag = sessionStorage.getItem("mobileProctoringVideo") === "true";

  const stepNumber = screenshotFlag || videoFlag ? 6 : 4;

  const handleIntroEnd = () => {
    setIsFirstWatch(false);
    setIsIntroStage(false);
    setIsReplayFullscreen(false);
  };

  const goFullscreenReplay = () => {
    setIsIntroStage(true);
    setIsReplayFullscreen(true);

    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
    }, 50);
  };

  const handleProceed = () => {
    navigate("/loader");
  };

  return (
    <div className="w-screen h-screen relative overflow-hidden">
      {/* ================= FULLSCREEN VIDEO ================= */}
      {isIntroStage && (
        <>
          <video
            ref={videoRef}
            className="w-full h-full object-contain bg-black"
            autoPlay
            controls
            disablePictureInPicture
            onEnded={handleIntroEnd}
          >
            <source
              src="https://t1developmentneedles.blob.core.windows.net/intro-videos/NH_Show_ID_Notification.mp4?sp=r&st=2026-07-14T09:49:59Z&se=2031-01-24T18:04:59Z&sv=2026-02-06&sr=b&sig=6smEboJz1XzNyKTN8MKYWngcd7Bl8u6Ow2d5HYfoGZI%3D"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>

          {/* Instruction only on first intro */}
          {!isReplayFullscreen && (
            <div
              className={`
                absolute left-1/2 -translate-x-1/2
                px-8 py-2 rounded-2xl
                bg-[#111729]/95 backdrop-blur-md text-center
                transition-all duration-700
                ${
                  showTopMode
                    ? "top-1 max-w-[720px]"
                    : "top-1/2 -translate-y-1/2 max-w-[720px]"
                }
              `}
            >
              {!showTopMode && (
                <p className="text-white app-video-overlay font-medium mb-2">
                  Please attentively watch this video…
                </p>
              )}
              <p className="text-white/90 app-body">
                You will be able to proceed once the video has ended.
              </p>
            </div>
          )}

          {/* Block seek bar on first watch only */}
          {isFirstWatch && (
            <div className="absolute bottom-0 left-0 w-full h-[50px] z-10" />
          )}

          {/* Replay fullscreen buttons */}
          {isReplayFullscreen && (
            <div className="absolute bottom-8 right-8 flex gap-3 z-20">
              <button
                onClick={() => {
                  if (videoRef.current) {
                    videoRef.current.currentTime = 0;
                    videoRef.current.play();
                  }
                }}
                className="btn-size flex bg-btnBgColor items-center px-6 py-1.5 text-white"
              >
                <img src={reloadImg} alt="reload" className="mr-2" />
                Play Again
              </button>

              <button
                onClick={handleProceed}
                className="flex items-center px-6 py-1.5 btn-size bg-btnBgColor text-white"
              >
                Proceed
                <img src={arrowImg} alt="arrow" className="ml-2" />
              </button>
            </div>
          )}
        </>
      )}

      {/* ================= CONTENT SCREEN ================= */}
      {!isIntroStage && (
        <div className="flex flex-col min-h-screen bg-white overflow-x-hidden px-4 pt-4">
          {/* Header */}
          <Header />

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col md:flex-row items-center justify-center px-1 pt-5 max-w-9xl mx-auto">
            {/* Left Section (Video) */}
            <div className="w-full md:w-1/2 flex justify-start items-center pl-[14px] pr-[30px]">
              <video
                ref={videoRef}
                className="w-full max-w-[900px] h-auto rounded-md shadow-md"
                controls
                preload="auto"
                playsInline
                disablePictureInPicture
                onPlay={goFullscreenReplay}
              >
                <source
                  src="https://t1developmentneedles.blob.core.windows.net/intro-videos/NH_Show_ID_Notification.mp4?sp=r&st=2026-07-14T09:49:59Z&se=2031-01-24T18:04:59Z&sv=2026-02-06&sr=b&sig=6smEboJz1XzNyKTN8MKYWngcd7Bl8u6Ow2d5HYfoGZI%3D"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Divider */}
            <div className="hidden md:block self-stretch w-[1px] bg-dividerColor mx-1 my-[5%]" />

            {/* Right Section */}
            <div className="w-full md:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-10 mt-6 md:mt-0">
              <div className="max-w-[720px]">
                <h2
                  className="font-semibold mb-4 leading-snug outer-font-color
      app-title"
                >
                  ID Display & Verification
                </h2>

                <p
                  className="mb-4 leading-relaxed outer-font-color
      app-body"
                >
                  To begin your assessment, you are required to verify your
                  Identity.
                </p>

                <p
                  className="leading-relaxed outer-font-color
    app-body"
                >
                  Please ensure you have your Government ID* handy. When you are
                  ready, just click on the{" "}
                  <span className="font-medium">"Proceed"</span> button.
                </p>
              </div>
            </div>
          </div>

          {/* Buttons container at the bottom */}
          <div className="absolute bottom-8 right-8 flex gap-3 z-20">
            <button
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.currentTime = 0;
                  videoRef.current.play();
                }
              }}
              className="btn-size flex bg-btnBgColor items-center px-6 py-1.5 text-white text-[16px]"
            >
              <img src={reloadImg} alt="reload" className="mr-2 w-4" />
              Play Again
            </button>

            <button
              onClick={handleProceed}
              className="btn-size flex items-center px-6 py-1.5 bg-btnBgColor text-white text-[16px]"
            >
              Proceed
              <img src={arrowImg} alt="arrow" className="ml-2" />
            </button>
          </div>

          {/* Footer */}
          <div className="w-full py-4 text-[10px] text-gray-600 text-center px-4">
            ©{" "}
            {new Date().getFullYear() > 2026
              ? `2026-${new Date().getFullYear()}`
              : "2026"}
            , Needles and Haystacks Technology Pvt Ltd.
          </div>
        </div>
      )}
    </div>
  );
};

export default Verification;
